import { useEffect, useRef, useState, type FormEvent } from 'react';
import { authApi, ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth.store';

/**
 * Forced password-change screen.
 *
 * Rendered by App.tsx when the authenticated admin's `must_change_password`
 * flag is true. There is no "skip" — the only way out is a successful change
 * (which invalidates the session server-side, so we kick back to login).
 */
export function ChangePasswordPage({ forced }: { forced: boolean }) {
  const me = useAuthStore((s) => (s.state.status === 'authenticated' ? s.state.me : null));
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const currentRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    currentRef.current?.focus();
  }, []);

  if (!me) return null;

  const checks = passwordChecks(newPassword);
  const allMet = checks.every((c) => c.ok);
  const matches = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = currentPassword.length > 0 && allMet && matches && !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      // Backend invalidates all sessions on password change. Kick back to
      // login with a friendly notice — the user will sign in fresh with the
      // new password and `must_change_password` will now be false.
      setUnauthenticated('Password updated — please sign in with your new password.');
    } catch (err) {
      const message = err instanceof ApiError
        ? err.body.message ?? err.body.error ?? 'Could not change password'
        : 'Could not change password';
      setError(message);
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-8">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md space-y-6 bg-surface-2 rounded-2xl p-8 shadow-2xl"
      >
        <div>
          <h1 className="font-heading text-xl font-semibold tracking-tight">
            {forced ? 'Set a new password' : 'Change password'}
          </h1>
          {forced && (
            <p className="mt-1 text-sm text-ink-muted">
              You're signed in with a temporary password. Choose a new one to continue.
            </p>
          )}
        </div>

        <div className="space-y-4">
          <Field label="Current password">
            <input
              ref={currentRef}
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full rounded-lg bg-surface-3 px-3 py-2.5 text-sm text-ink outline-none ring-1 ring-transparent focus:ring-primary"
            />
          </Field>

          <Field label="New password">
            <input
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg bg-surface-3 px-3 py-2.5 text-sm text-ink outline-none ring-1 ring-transparent focus:ring-primary"
            />
          </Field>

          <ul className="text-xs space-y-1 pl-1">
            {checks.map((c) => (
              <li key={c.label} className={c.ok ? 'text-good' : 'text-ink-muted'}>
                <span className="inline-block w-4">{c.ok ? '✓' : '○'}</span>
                {c.label}
              </li>
            ))}
          </ul>

          <Field label="Confirm new password">
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg bg-surface-3 px-3 py-2.5 text-sm text-ink outline-none ring-1 ring-transparent focus:ring-primary"
            />
            {confirmPassword.length > 0 && !matches && (
              <p className="text-xs text-bad mt-1">Passwords do not match</p>
            )}
          </Field>
        </div>

        {error && (
          <p role="alert" className="text-sm text-bad">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full rounded-lg bg-primary px-4 py-2.5 font-medium text-ink transition disabled:opacity-50"
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase tracking-wider text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

interface PasswordCheck {
  label: string;
  ok: boolean;
}

// Mirror server-side validatePasswordStrength() — see src/shared/auth/passwords.ts
function passwordChecks(pw: string): PasswordCheck[] {
  return [
    { label: 'At least 8 characters', ok: pw.length >= 8 },
    { label: 'Contains an uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'Contains a lowercase letter', ok: /[a-z]/.test(pw) },
    { label: 'Contains a digit', ok: /\d/.test(pw) },
  ];
}
